(() => {
  'use strict';

  const GLOBAL_KEY = 'RFNProductReviews';
  const ROOT_SELECTOR = '[data-rfn-product-reviews]';
  const CACHE_PREFIX = 'rfnFeraPublicMedia:v1.8:';
  const CACHE_TTL = 30 * 60 * 1000;
  const API_BASE = 'https://api.fera.ai/v3/public/products';
  const API_PAGE_SIZE = 12;
  const EMBLA_SRC =
    'https://unpkg.com/embla-carousel@8.6.0/embla-carousel.umd.js';

  if (window[GLOBAL_KEY]?.initialized) {
    window[GLOBAL_KEY].refresh();
    return;
  }

  const instances = new WeakMap();

  const loadEmbla = () => {
    if (window.EmblaCarousel) {
      return Promise.resolve(window.EmblaCarousel);
    }

    if (window.__rfnEmblaLoading) {
      return window.__rfnEmblaLoading;
    }

    window.__rfnEmblaLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = EMBLA_SRC;
      script.async = true;
      script.onload = () => resolve(window.EmblaCarousel);
      script.onerror = () => reject(
        new Error('Unable to load Embla Carousel.')
      );
      document.head.appendChild(script);
    });

    return window.__rfnEmblaLoading;
  };

  const getNestedValue = (object, path) => {
    if (!object || !path) return undefined;

    return path.split('.').reduce((value, key) => {
      if (value === undefined || value === null) return undefined;
      return value[key];
    }, object);
  };

  const firstValue = (object, paths) => {
    for (const path of paths) {
      const value = getNestedValue(object, path);

      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return '';
  };

  const normalizeMedia = (item, index) => {
    if (!item || typeof item !== 'object') return null;

    const rawType = firstValue(item, [
      'type',
      'media_type',
      'content_type',
      'kind'
    ]);

    const type = String(rawType || 'photo').toLowerCase();

    const rawUrl = firstValue(item, [
      'url',
      'original_url',
      'large_url',
      'image_url',
      'video_url',
      'media_url',
      'file_url',
      'src',
      'urls.original',
      'urls.large',
      'urls.full',
      'media.url'
    ]);

    const rawThumbnailUrl = firstValue(item, [
      'thumbnail_url',
      'thumb_url',
      'preview_url',
      'small_url',
      'image_thumbnail_url',
      'urls.thumbnail',
      'urls.thumb',
      'urls.small',
      'media.thumbnail_url'
    ]);

    if (typeof rawUrl !== 'string' || !rawUrl.trim()) return null;

    const url = rawUrl.trim();
    const thumbnailUrl =
      typeof rawThumbnailUrl === 'string' && rawThumbnailUrl.trim()
        ? rawThumbnailUrl.trim()
        : url;

    const id = String(firstValue(item, [
      'id',
      '_id',
      'media_id'
    ]) || `${index}-${url}`);

    const caption = String(firstValue(item, [
      'caption',
      'description',
      'body',
      'heading',
      'review.body'
    ]) || '');

    const createdAt = firstValue(item, [
      'created_at',
      'createdAt'
    ]);

    return {
      id,
      type: type.includes('video') ? 'video' : 'photo',
      url,
      thumbnailUrl,
      caption,
      createdAt: createdAt ? String(createdAt) : ''
    };
  };

  const normalizePayload = (payload, maxItems) => {
    const source = Array.isArray(payload)
      ? payload
      : (
          Array.isArray(payload?.data) ? payload.data :
          Array.isArray(payload?.media) ? payload.media :
          Array.isArray(payload?.items) ? payload.items :
          []
        );

    const seen = new Set();

    return source
      .map(normalizeMedia)
      .filter(Boolean)
      .filter((item) => {
        if (seen.has(item.url)) return false;
        seen.add(item.url);
        return true;
      })
      .sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return new Date(b.createdAt) - new Date(a.createdAt);
      })
      .slice(0, maxItems);
  };

  const getCachedMedia = (productId) => {
    try {
      const raw = sessionStorage.getItem(`${CACHE_PREFIX}${productId}`);
      if (!raw) return null;

      const parsed = JSON.parse(raw);

      if (!parsed?.createdAt || !Array.isArray(parsed?.items)) {
        return null;
      }

      if (Date.now() - parsed.createdAt > CACHE_TTL) {
        sessionStorage.removeItem(`${CACHE_PREFIX}${productId}`);
        return null;
      }

      return parsed.items;
    } catch (error) {
      return null;
    }
  };

  const cacheMedia = (productId, items) => {
    try {
      sessionStorage.setItem(
        `${CACHE_PREFIX}${productId}`,
        JSON.stringify({
          createdAt: Date.now(),
          items
        })
      );
    } catch (error) {
      // The section still works without session storage.
    }
  };

  const getPayloadItems = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.media)) return payload.media;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  };

  const getPayloadTotal = (payload) => {
    const value =
      payload?.meta?.total_count ??
      payload?.meta?.total ??
      payload?.pagination?.total ??
      payload?.total_count ??
      payload?.total;

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const fetchProductMediaPage = async ({
    productId,
    publicKey,
    offset
  }) => {
    if (!publicKey) {
      throw new Error('Fera Public API key is missing.');
    }

    const url = new URL(
      `${API_BASE}/${encodeURIComponent(productId)}/media`
    );

    /*
      Fera's public media endpoint currently returns up to 12 media
      records per response, so larger gallery limits must be paginated.
    */
    url.searchParams.set('public_key', publicKey);
    url.searchParams.set('limit', String(API_PAGE_SIZE));
    url.searchParams.set('offset', String(offset));
    url.searchParams.set('subject', 'product');
    url.searchParams.set('sort_by', 'created_at:desc');

    const response = await fetch(url.toString(), {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        Accept: 'application/json'
      }
    });

    const rawText = await response.text();
    let payload = null;

    try {
      payload = rawText ? JSON.parse(rawText) : {};
    } catch (error) {
      throw new Error(
        `Fera returned a non-JSON response (${response.status}).`
      );
    }

    if (!response.ok) {
      const apiMessage =
        payload?.message ||
        payload?.error ||
        payload?.errors?.[0]?.message ||
        `HTTP ${response.status}`;

      throw new Error(`Fera Public API: ${apiMessage}`);
    }

    return payload;
  };

  const fetchProductMedia = async ({
    productId,
    publicKey,
    limit
  }) => {
    const target = Math.min(
      Math.max(Number(limit) || API_PAGE_SIZE, 1),
      36
    );

    const collected = [];
    const seenIds = new Set();
    let offset = 0;
    let knownTotal = null;

    while (collected.length < target) {
      const payload = await fetchProductMediaPage({
        productId,
        publicKey,
        offset
      });

      const pageItems = getPayloadItems(payload);

      if (!pageItems.length) break;

      let addedThisPage = 0;

      pageItems.forEach((item, index) => {
        const identity = String(
          item?.id ??
          item?._id ??
          item?.media_id ??
          item?.url ??
          item?.original_url ??
          `${offset}-${index}`
        );

        if (seenIds.has(identity)) return;

        seenIds.add(identity);
        collected.push(item);
        addedThisPage += 1;
      });

      knownTotal = getPayloadTotal(payload) ?? knownTotal;
      offset += pageItems.length;

      if (addedThisPage === 0) break;
      if (knownTotal !== null && offset >= knownTotal) break;
      if (pageItems.length < API_PAGE_SIZE) break;
    }

    return collected.slice(0, target);
  };

  const createCard = (item, index) => {
    const article = document.createElement('article');
    article.className = 'rfn-product-reviews__card';
    article.dataset.reviewIndex = String(index);

    if (item.type === 'video') {
      const video = document.createElement('video');
      video.className = 'rfn-product-reviews__media';
      video.src = item.url;
      video.poster = item.thumbnailUrl;
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.draggable = false;
      video.setAttribute('draggable', 'false');
      video.setAttribute('controlsList', 'nodownload');
      article.appendChild(video);
    } else {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rfn-product-reviews__media-button';
      button.dataset.rfnReviewOpen = item.url;
      button.setAttribute(
        'aria-label',
        `Open customer review image ${index + 1}`
      );

      const image = document.createElement('img');
      image.className = 'rfn-product-reviews__media';
      image.src = item.thumbnailUrl || item.url;
      image.dataset.fullSrc = item.url;
      image.alt = item.caption || `Customer review ${index + 1}`;
      image.loading = index < 2 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      image.setAttribute('draggable', 'false');

      image.addEventListener('load', () => {
        const ratio = image.naturalWidth / image.naturalHeight;

        article.classList.toggle('is-square', ratio >= 0.92);
        article.classList.toggle('is-portrait', ratio < 0.92);
      }, { once: true });

      button.appendChild(image);
      article.appendChild(button);
    }

    return article;
  };

  const isDesktop = () => (
    window.matchMedia('(min-width: 901px)').matches
  );

  const getEmblaOptions = () => ({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
    skipSnaps: false,
    duration: isDesktop() ? 34 : 30,
    slidesToScroll: isDesktop() ? 2 : 1,
    watchDrag: true
  });

  const createInstance = (root) => {
    if (!root || instances.has(root)) return instances.get(root);

    const productId = root.dataset.productId;
    const publicKey = root.dataset.publicKey || '';
    const designMode = root.dataset.designMode === 'true';
    const maxItems = Number(root.dataset.maxItems || 12);
    const expandLabel =
      root.dataset.expandLabel || 'ดูรีวิวทั้งหมด';
    const collapseLabel =
      root.dataset.collapseLabel || 'ย่อรีวิว';

    const viewport = root.querySelector('[data-rfn-review-viewport]');
    const track = root.querySelector('[data-rfn-review-track]');
    const dots = root.querySelector('[data-rfn-review-dots]');
    const previous = root.querySelector('[data-rfn-review-prev]');
    const next = root.querySelector('[data-rfn-review-next]');
    const toggle = root.querySelector('[data-rfn-review-toggle]');
    const status = root.querySelector('[data-rfn-review-status]');
    const lightbox = root.querySelector('[data-rfn-review-lightbox]');
    const lightboxImage =
      root.querySelector('[data-rfn-review-lightbox-image]');
    const lightboxClose =
      root.querySelector('[data-rfn-review-lightbox-close]');

    if (
      !viewport ||
      !track ||
      !dots ||
      !previous ||
      !next ||
      !toggle
    ) {
      return null;
    }

    let media = [];
    let embla = null;
    let expanded = false;
    let resizeTimer = null;

    /*
      Dedicated tap detector for review zoom.
      Embla owns dragging; this only distinguishes a real tap/click
      from a swipe before opening the lightbox.
    */
    let zoomPointer = null;
    let suppressClickUntil = 0;

    const setStatus = (message) => {
      if (status) status.textContent = message || '';
    };

    const showDebugError = (error) => {
      console.warn('[RFN Product Reviews v1.8]', error);

      if (designMode) {
        root.hidden = false;
        root.classList.add('has-error');
        setStatus(error.message || 'Unable to load Fera reviews.');
      } else {
        root.hidden = true;
        setStatus('');
      }
    };

    const openLightbox = (src, alt = '') => {
      if (!lightbox || !lightboxImage) return;

      lightboxImage.src = src;
      lightboxImage.alt = alt;
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add(
        'rfn-review-lightbox-open'
      );
    };

    const closeLightbox = () => {
      if (!lightbox || !lightboxImage) return;

      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      lightboxImage.src = '';
      document.documentElement.classList.remove(
        'rfn-review-lightbox-open'
      );
    };

    const renderDots = () => {
      dots.innerHTML = '';

      const snapCount = embla
        ? embla.scrollSnapList().length
        : 0;

      for (let index = 0; index < snapCount; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'rfn-product-reviews__dot';
        dot.dataset.page = String(index);
        dot.setAttribute(
          'aria-label',
          `Go to review page ${index + 1}`
        );
        dots.appendChild(dot);
      }
    };

    const syncControls = () => {
      if (!embla || expanded) return;

      const selected = embla.selectedScrollSnap();

      dots.querySelectorAll('[data-page]').forEach((dot, index) => {
        const active = index === selected;
        dot.classList.toggle('is-active', active);

        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      previous.disabled = !embla.canScrollPrev();
      next.disabled = !embla.canScrollNext();
    };

    const bindEmblaEvents = () => {
      if (!embla) return;

      const setDragging = () => {
        track.classList.add('is-dragging');
      };

      const unsetDragging = () => {
        track.classList.remove('is-dragging');
      };

      embla
        .on('select', syncControls)
        .on('reInit', () => {
          renderDots();
          syncControls();
        })
        .on('resize', syncControls)
        .on('pointerDown', setDragging)
        .on('pointerUp', unsetDragging)
        .on('settle', unsetDragging);
    };

    const initCarousel = async ({
      startAt = 0,
      jump = true
    } = {}) => {
      if (expanded || !media.length) return;

      await loadEmbla();

      if (embla) {
        embla.reInit(getEmblaOptions());
      } else {
        embla = window.EmblaCarousel(
          viewport,
          getEmblaOptions()
        );
        bindEmblaEvents();
      }

      renderDots();
      embla.scrollTo(startAt, jump);
      syncControls();
      root.classList.add('is-embla-ready');
    };

    const destroyCarousel = () => {
      if (!embla) return;

      embla.destroy();
      embla = null;
      track.classList.remove('is-dragging');
      root.classList.remove('is-embla-ready');
    };

    const render = async () => {
      destroyCarousel();
      track.innerHTML = '';

      media.forEach((item, index) => {
        track.appendChild(createCard(item, index));
      });

      root.hidden = false;
      root.classList.remove('has-error');
      root.classList.add('is-ready');

      const itemsPerPage = isDesktop() ? 2 : 1;
      toggle.hidden = media.length <= itemsPerPage;

      setStatus('');

      try {
        await initCarousel({
          startAt: 0,
          jump: true
        });
      } catch (error) {
        /*
          Fallback remains usable with native horizontal scrolling.
          The section itself still displays.
        */
        root.classList.add('is-carousel-fallback');
        dots.hidden = true;
        previous.hidden = true;
        next.hidden = true;
        console.warn('[RFN Product Reviews v1.8]', error);
      }
    };

    const setExpanded = async (value) => {
      expanded = Boolean(value);
      root.classList.toggle('is-expanded', expanded);

      toggle.textContent =
        expanded ? collapseLabel : expandLabel;

      toggle.setAttribute(
        'aria-expanded',
        expanded ? 'true' : 'false'
      );

      if (expanded) {
        destroyCarousel();
        viewport.scrollLeft = 0;
        dots.innerHTML = '';
        previous.disabled = true;
        next.disabled = true;
        return;
      }

      window.requestAnimationFrame(() => {
        initCarousel({
          startAt: 0,
          jump: true
        }).catch(showDebugError);
      });
    };

    const load = async ({ ignoreCache = false } = {}) => {
      if (!productId) {
        showDebugError(new Error('Shopify product ID is missing.'));
        return;
      }

      setStatus('Loading customer reviews…');

      if (!ignoreCache) {
        const cached = getCachedMedia(productId);

        if (cached?.length) {
          media = cached.slice(0, maxItems);
          await render();
          return;
        }
      }

      try {
        const payload = await fetchProductMedia({
          productId,
          publicKey,
          limit: maxItems
        });

        media = normalizePayload(payload, maxItems);

        if (!media.length) {
          if (designMode) {
            root.hidden = false;
            setStatus(
              `No approved Fera photo or video was found for product ${productId}.`
            );
          } else {
            root.hidden = true;
            setStatus('');
          }
          return;
        }

        cacheMedia(productId, media);
        await render();
      } catch (error) {
        showDebugError(error);
      }
    };

    previous.addEventListener('click', () => {
      embla?.scrollPrev();
    });

    next.addEventListener('click', () => {
      embla?.scrollNext();
    });

    dots.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-page]');
      if (!dot || !embla) return;

      embla.scrollTo(Number(dot.dataset.page || 0));
    });

    toggle.addEventListener('click', () => {
      setExpanded(!expanded);
    });

    /*
      Zoom interaction for both Carousel and Expanded modes.

      Pointer movement greater than the threshold is treated as a swipe,
      so it will never open the lightbox. A stationary tap/click opens
      the image immediately on pointer release.
    */
    viewport.addEventListener(
      'pointerdown',
      (event) => {
        const trigger =
          event.target.closest('[data-rfn-review-open]');

        if (!trigger) return;

        if (
          event.pointerType === 'mouse' &&
          event.button !== 0
        ) {
          return;
        }

        zoomPointer = {
          id: event.pointerId,
          trigger,
          startX: event.clientX,
          startY: event.clientY,
          moved: false
        };
      },
      { capture: true }
    );

    viewport.addEventListener(
      'pointermove',
      (event) => {
        if (
          !zoomPointer ||
          event.pointerId !== zoomPointer.id
        ) {
          return;
        }

        const distanceX =
          Math.abs(event.clientX - zoomPointer.startX);
        const distanceY =
          Math.abs(event.clientY - zoomPointer.startY);

        if (distanceX > 8 || distanceY > 8) {
          zoomPointer.moved = true;
        }
      },
      { capture: true, passive: true }
    );

    viewport.addEventListener(
      'pointerup',
      (event) => {
        if (
          !zoomPointer ||
          event.pointerId !== zoomPointer.id
        ) {
          return;
        }

        const interaction = zoomPointer;
        zoomPointer = null;

        /*
          Block the synthetic click that browsers emit after pointerup.
          This also prevents a completed swipe from opening the image.
        */
        suppressClickUntil = Date.now() + 450;

        if (interaction.moved) return;

        const image = interaction.trigger.querySelector('img');

        openLightbox(
          interaction.trigger.dataset.rfnReviewOpen,
          image?.alt || ''
        );
      },
      { capture: true }
    );

    const cancelZoomPointer = (event) => {
      if (
        !zoomPointer ||
        event.pointerId !== zoomPointer.id
      ) {
        return;
      }

      zoomPointer = null;
      suppressClickUntil = Date.now() + 450;
    };

    viewport.addEventListener(
      'pointercancel',
      cancelZoomPointer,
      { capture: true }
    );

    viewport.addEventListener(
      'lostpointercapture',
      cancelZoomPointer,
      { capture: true }
    );

    /*
      Keyboard accessibility and fallback for browsers that don't
      dispatch Pointer Events. Pointer-generated clicks are ignored
      because the pointerup handler above already handled them.
    */
    track.addEventListener('click', (event) => {
      const trigger =
        event.target.closest('[data-rfn-review-open]');

      if (!trigger) return;

      if (Date.now() < suppressClickUntil) {
        event.preventDefault();
        return;
      }

      const image = trigger.querySelector('img');

      openLightbox(
        trigger.dataset.rfnReviewOpen,
        image?.alt || ''
      );
    });

    track.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    lightboxClose?.addEventListener('click', closeLightbox);

    /*
      Close the lightbox when the customer taps/clicks anywhere outside
      the review image. The close button and Escape key remain available.
    */
    lightbox?.addEventListener('click', (event) => {
      const clickedImage = event.target.closest(
        '[data-rfn-review-lightbox-image]'
      );

      if (clickedImage) return;

      closeLightbox();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeLightbox();
    });

    window.addEventListener(
      'resize',
      () => {
        window.clearTimeout(resizeTimer);

        resizeTimer = window.setTimeout(() => {
          if (!expanded && embla) {
            embla.reInit(getEmblaOptions());
            renderDots();
            syncControls();
          }
        }, 120);
      },
      { passive: true }
    );

    const instance = {
      refresh: () => {
        if (!media.length || expanded) return;

        if (embla) {
          embla.reInit(getEmblaOptions());
          renderDots();
          syncControls();
        }
      },

      reload: () => {
        sessionStorage.removeItem(
          `${CACHE_PREFIX}${productId}`
        );

        return load({ ignoreCache: true });
      },

      destroy: () => {
        destroyCarousel();
        instances.delete(root);
      }
    };

    instances.set(root, instance);
    load();

    return instance;
  };

  const init = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(createInstance);
  };

  const refresh = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const instance =
        instances.get(root) || createInstance(root);

      instance?.refresh();
    });
  };

  window[GLOBAL_KEY] = {
    initialized: true,
    init,
    refresh
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(),
      { once: true }
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => init(event.target)
  );
})();
